import React, { useCallback, useEffect, useState } from 'react';
import { useApiAuth } from '../../hooks/useApiAuth';

const DEFAULT_PROCESS = [
  { step: '01', title: 'Share', description: 'Tell us your ideas and vision.' },
  { step: '02', title: 'Design', description: 'We plan and confirm your custom piece.' },
  { step: '03', title: 'Craft', description: 'Handmade with care and quality materials.' },
  { step: '04', title: 'Enjoy', description: 'Receive and treasure your unique creation.' },
];

export default function ServicesManager({ setStatus }) {
  const { authFetch } = useApiAuth();
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [process, setProcess] = useState(DEFAULT_PROCESS);
  const [form, setForm] = useState({ title: '', description: '', bullets: [] });

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/api/admin/services');
      setItems((data.items || []).map((s) => ({ ...s, bullets: Array.isArray(s.bullets) ? s.bullets : [] })));
      const cfg = await authFetch('/api/admin/config');
      setProcess(Array.isArray(cfg.services_process) && cfg.services_process.length ? cfg.services_process : DEFAULT_PROCESS);
    } catch (e) { setStatus(`Load error: ${e.message}`); }
  }, [authFetch, setStatus]);

  useEffect(() => { load(); }, [load]);

  const addService = async () => {
    if (!form.title.trim()) { setStatus('Title required.'); return; }
    setBusy(true); setStatus('Adding…');
    try {
      const sort_order = items.length;
      await authFetch('/api/admin/services', {
        method: 'POST',
        body: { title: form.title, description: form.description, bullets: form.bullets.filter(Boolean), sort_order },
      });
      setForm({ title: '', description: '', bullets: [] });
      await load();
      setStatus('Added.');
    } catch (e) { setStatus(`Add error: ${e.message}`); }
    setBusy(false);
  };

  const updItem = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    setItems(next);
  };

  const saveItem = async (item) => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch(`/api/admin/services?id=${item.id}`, {
        method: 'PUT',
        body: { title: item.title, description: item.description, bullets: (item.bullets || []).filter(Boolean), sort_order: item.sort_order || 0 },
      });
      setStatus('Saved.');
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    setBusy(true);
    try {
      await authFetch(`/api/admin/services?id=${id}`, { method: 'DELETE' });
      await load();
      setStatus('Deleted.');
    } catch (e) { setStatus(`Delete error: ${e.message}`); }
    setBusy(false);
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[i]; const b = items[j];
    const next = items.slice();
    next[i] = { ...b, sort_order: i };
    next[j] = { ...a, sort_order: j };
    setItems(next);
    setBusy(true);
    try {
      await authFetch('/api/admin/services', {
        method: 'PATCH',
        body: { items: [{ id: a.id, sort_order: j }, { id: b.id, sort_order: i }] },
      });
    } catch (e) { setStatus(`Reorder error: ${e.message}`); }
    setBusy(false);
  };

  const saveProcess = async () => {
    setBusy(true); setStatus('Saving…');
    try {
      await authFetch('/api/admin/config', { method: 'PUT', body: { key: 'services_process', value: process } });
      setStatus('Saved process.');
    } catch (e) { setStatus(`Save error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div>
      <section className="admin-section">
        <h2>Add a new service</h2>
        <div className="admin-form">
          <label>Title
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label>Description
            <textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <div>
            <strong>Bullets</strong>
            {form.bullets.map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input type="text" value={b} onChange={(e) => { const next = form.bullets.slice(); next[i] = e.target.value; setForm({ ...form, bullets: next }); }} style={{ flex: 1 }} />
                <button className="admin-btn" onClick={() => setForm({ ...form, bullets: form.bullets.filter((_, j) => j !== i) })}>×</button>
              </div>
            ))}
            <button className="admin-btn" style={{ marginTop: 6 }} onClick={() => setForm({ ...form, bullets: [...form.bullets, ''] })}>+ Bullet</button>
          </div>
          <button className="admin-btn admin-btn-primary" disabled={busy} onClick={addService}>Add service</button>
        </div>
      </section>

      <section className="admin-section">
        <h2>Services ({items.length})</h2>
        {items.map((s, i) => (
          <div key={s.id} className="admin-card" style={{ marginBottom: 12 }}>
            <label>Title
              <input type="text" value={s.title || ''} onChange={(e) => updItem(i, { title: e.target.value })} />
            </label>
            <label>Description
              <textarea rows="2" value={s.description || ''} onChange={(e) => updItem(i, { description: e.target.value })} />
            </label>
            <div>
              <strong>Bullets</strong>
              {(s.bullets || []).map((b, k) => (
                <div key={k} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <input type="text" value={b} onChange={(e) => { const nb = (s.bullets || []).slice(); nb[k] = e.target.value; updItem(i, { bullets: nb }); }} style={{ flex: 1 }} />
                  <button className="admin-btn" onClick={() => updItem(i, { bullets: (s.bullets || []).filter((_, m) => m !== k) })}>×</button>
                </div>
              ))}
              <button className="admin-btn" style={{ marginTop: 6 }} onClick={() => updItem(i, { bullets: [...(s.bullets || []), ''] })}>+ Bullet</button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="admin-btn" disabled={busy || i === 0} onClick={() => move(i, -1)}>↑ Up</button>
              <button className="admin-btn" disabled={busy || i === items.length - 1} onClick={() => move(i, 1)}>↓ Down</button>
              <button className="admin-btn admin-btn-primary" disabled={busy} onClick={() => saveItem(s)}>Save</button>
              <button className="admin-btn admin-btn-danger" disabled={busy} onClick={() => removeItem(s.id)}>Delete</button>
            </div>
          </div>
        ))}
      </section>

      <section className="admin-section">
        <h2>Our Process steps</h2>
        <div className="admin-form">
          {process.map((p, i) => (
            <div key={i} className="admin-card">
              <label>Step number (like 01, 02, …)
                <input type="text" value={p.step || ''} onChange={(e) => { const n = process.slice(); n[i] = { ...n[i], step: e.target.value }; setProcess(n); }} />
              </label>
              <label>Title
                <input type="text" value={p.title || ''} onChange={(e) => { const n = process.slice(); n[i] = { ...n[i], title: e.target.value }; setProcess(n); }} />
              </label>
              <label>Description
                <textarea rows="2" value={p.description || ''} onChange={(e) => { const n = process.slice(); n[i] = { ...n[i], description: e.target.value }; setProcess(n); }} />
              </label>
              <button className="admin-btn" onClick={() => setProcess(process.filter((_, j) => j !== i))}>Remove</button>
            </div>
          ))}
          <button className="admin-btn" onClick={() => setProcess([...process, { step: '', title: '', description: '' }])}>+ Add step</button>
          <button className="admin-btn admin-btn-primary" disabled={busy} onClick={saveProcess}>Save process</button>
        </div>
      </section>
    </div>
  );
}
